// src/hooks/useUnreadCount.ts
import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { workersClient } from "@/integrations/database/client";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Hook to get total unread messages count across all chats for current user
 * OPTIMIZED: Uses only real-time subscriptions, no polling
 * Updates in real-time via Supabase subscriptions
 */
export const useUnreadCount = () => {
  const { user } = useAuth();

  const { data: unreadCount = 0, refetch } = useQuery({
    queryKey: ["unread-count", user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;

      // Get all chats where user is participant
      const { data: chats, error: chatsError } = await supabase
        .from("chats")
        .select("id")
        .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`);

      if (chatsError || !chats) {
        console.error("[useUnreadCount] Error fetching chats:", chatsError);
        return 0;
      }

      const chatIds = chats.map((chat) => chat.id);

      if (chatIds.length === 0) return 0;

      // Count unread messages where user is NOT the sender
      const { count, error: messagesError } = await supabase
        .from("messages")
        .select("id", { count: "exact", head: true })
        .in("chat_id", chatIds)
        .eq("is_read", false)
        .neq("sender_id", user.id);

      if (messagesError) {
        console.error(
          "[useUnreadCount] Error counting unread messages:",
          messagesError,
        );
        return 0;
      }

      return count || 0;
    },
    enabled: !!user?.id,
    // OPTIMIZATION: Disabled polling - rely only on real-time subscriptions
    // This eliminates 550k+ requests/day for 100 users (was: refetchInterval: 30000)
    refetchInterval: false,
    // OPTIMIZATION: Increased staleTime from 10s to 1 minute
    staleTime: 60000, // 1 minute - data is kept fresh by real-time updates
    gcTime: 5 * 60 * 1000,
  });

  // Setup real-time subscription for new messages
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel("unread-count-updates")
      .on(
        "postgres_changes",
        {
          event: "*", // INSERT, UPDATE, DELETE
          schema: "public",
          table: "messages",
        },
        () => {
          // Refetch count whenever messages table changes
          refetch();
        },
      )
      .subscribe();

    return () => {
      workersClient.removeChannel(channel);
    };
  }, [user?.id, refetch]);

  return { unreadCount, refetch };
};
